import { GameRoundServiceEvent, IServices } from "@netent/origin";
import { DisplayMode, IPlayUIState, View } from "@netent/playui";
import { ListenerCreator } from "../../types";

// When a game round starts:
// * Any open menus should be closed (by setting the view to default)
// * Menu and setting buttons must be deactivated, except quickspin
// * The spin button should turn into a quickstop button if that feature is available
// * Balance should be deducted and the win restored
const started = (services: IServices) => (): IPlayUIState => ({
  spin: {
    enabled: services.spin.isQuickStopAvailable(),
    spinning: true
  },
  bet: {
    enabled: false
  },
  menu: {
    enabled: false
  },
  autoplay: services.autoplay.isAvailable()
    ? {
        enabled: false
      }
    : null,
  balance: {
    valueInCash: services.money.getBalanceCash(),
    valueInCoins: services.money.getBalanceCoins()
  },
  win: {
    valueInCoins: services.win.getWinCoins(),
    valueInCash: services.win.getWinCash(),
    denomination: services.bet.getDenomination()
  },
  view: View.DEFAULT
});

// When a game round ends:
// * The display mode should be restored so that the keypad buttons
//   are accessible again if PlayUI was in minimal mode
// * The spin button should be restored
// * Menu and setting buttons should be available again
// * Balance and win should be updated with the results
const ended = (services: IServices) => (): IPlayUIState => {
  const isFreeRoundActive = services.freeRounds.isActive();
  const isAutoPlaying = services.autoplay.isActive();

  const playUIState: IPlayUIState = {
    displayMode: DisplayMode.DEFAULT,
    spin: {
      enabled: true,
      spinning: false
    },
    bet: {
      enabled: !isFreeRoundActive && !isAutoPlaying
    },
    menu: {
      enabled: !isAutoPlaying
    },
    autoplay: services.autoplay.isAvailable()
      ? {
          enabled: !isAutoPlaying && services.autoplay.isEnabled()
        }
      : null,
    balance: {
      valueInCash: services.money.getBalanceCash(),
      valueInCoins: services.money.getBalanceCoins()
    }
  };

  return playUIState;
};

const presentationEnded = (services: IServices) => (
  isGameOver: boolean
): IPlayUIState => {
  const isFreeRoundActive = services.freeRounds.isActive();
  const playUIState: IPlayUIState = {};
  if (isFreeRoundActive && isGameOver) {
    playUIState.freeRounds = {
      value: services.freeRounds.getRemaining(),
      enabled: true
    };
  }
  return playUIState;
};

const stopped = () => (): IPlayUIState => ({
  menu: {
    enabled: false
  },
  volume: {
    enabled: false
  },
  spin: {
    enabled: false
  },
  quickSpin: {
    enabled: false
  },
  bet: {
    enabled: false
  },
  autoplay: {
    enabled: false,
    running: false
  }
});

export const listeners = new Map<GameRoundServiceEvent, ListenerCreator>([
  ["ended", ended],
  ["started", started],
  ["stopped", stopped],
  ["presentationEnded", presentationEnded]
]);
